import React from 'react';
import ReactDOM from 'react-dom/client';
import 'react-app-polyfill/ie11';
import 'core-js';

import { CssBaseline } from '@mui/material';

import { FormProvider } from 'contexts/FormProvider';
import { ThemeProvider, qbeTheme, ADSLocaleProvider } from '@qbe/ads-core';
import { AllowedFileTypesDev } from 'utils/constants';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import './index.css';
import App from './App';

import { Provider } from 'react-redux';
import { store } from './store';
import { getLDProvider } from './contexts/LaunchDarklyProvider';

async function enableMocking() {
  // Only enable mocks in development unless explicitly disabled
  if (process.env.NODE_ENV !== 'development' || process.env.REACT_APP_DISABLE_MOCKS === 'true') {
    return;
  }

  const { worker } = await import('./mocks/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start();
}

// Get default white labelling partner from pathname if valid
const pathname = window.location.pathname;
// const defaultBrand = pathname.length > 1 ? (pathname.substr(pathname.lastIndexOf('/') + 1) as Brand) : Brand.QBE;

if (process.env.NODE_ENV === 'development') {
  // for api url submission
  window._CMS_ = {
    APIM: {
      baseUrl: 'https://dev.api-au.qbe.com/digital-address-api/v1',
    },
    API: {
      baseurl: 'https://sit.api-ext.qbe.com.au',
    },

    ENV: 'local',
    MAX_FILE_UPLOAD_SIZE_IN_MB: 10,
    ENABLE_FILE_UPLOAD: true,
    ALLOWED_FILE_EXTENSIONS: AllowedFileTypesDev,
    ldClientId: '68788d27abd0fd09233eac3c',
    recaptchaKey: '6LdnYnobAAAAAP194y4KCE3F5p01dlQU2ptC7nmy',
  };
}
const container = document.getElementById('root');
if (!container) throw new Error("The root container wasn't found");

const root = ReactDOM.createRoot(container);
type LaunchDarklyProviderComponent = (props: { children: React.ReactNode }) => React.ReactElement;

const renderApp = (LDProviderComponent?: LaunchDarklyProviderComponent) => {
  const appContent = (
    <FormProvider>
      <App />
    </FormProvider>
  );

  root.render(
    <React.StrictMode>
      <CssBaseline />
      <ADSLocaleProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={qbeTheme}>
          <Provider store={store}>
            {LDProviderComponent ? <LDProviderComponent>{appContent}</LDProviderComponent> : appContent}
          </Provider>
        </ThemeProvider>
      </ADSLocaleProvider>
    </React.StrictMode>
  );
};

enableMocking()
  .then(() => {
    // TODO: migrate over to `createRoot`
    // eslint-disable-next-line react/no-deprecated
    (async () => {
      try {
        const LDProvider = await getLDProvider();

        if (!LDProvider) {
          console.error('LaunchDarkly provider failed to initialize');
          renderApp();
          return;
        }

        renderApp(LDProvider);
      } catch (error) {
        console.error('Failed to initialize LaunchDarkly provider:', error);
        renderApp();
      }
    })();
  })
  .catch((error) => {
    console.error('Failed to initialize mock service worker:', error);
    renderApp();
  });

// Hot Module Replacement
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hotModule = module as any;
if (hotModule.hot && process.env.NODE_ENV === 'development') {
  hotModule.hot.accept();
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
